/** Helpers for reading the raw openapi documents shared by the module explorer, filter and example generator */

import { isModuleId, type ModuleId } from './module-labels';

export type JsonObject = Record<string, unknown>;

export const isObject = (value: unknown): value is JsonObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const HTTP_METHODS = ['get', 'put', 'post', 'delete', 'options', 'head', 'patch', 'trace'];

export const NO_MODULES: ReadonlySet<ModuleId> = new Set();

/**
 * The modules in an x-modules value. The build rejects specs with unknown modules (see validateModules in
 * scripts/build-openapi.ts), so nothing is expected to be dropped here.
 */
export const toModules = (value: unknown): ModuleId[] => (Array.isArray(value) ? value.filter(isModuleId) : []);

const COMPONENT_REF_PREFIX = '#/components/schemas/';

/** The document's component schemas, which $refs point into */
export const getComponentSchemas = (spec: JsonObject) =>
  (spec.components as JsonObject | undefined)?.schemas as JsonObject | undefined;

/** The component schema name a $ref points at, or undefined when it is not a component schema $ref */
export const getRefName = (ref: unknown) =>
  typeof ref === 'string' && ref.startsWith(COMPONENT_REF_PREFIX) ? ref.slice(COMPONENT_REF_PREFIX.length) : undefined;

const nameCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/** Orders names for display in trees, naturally so scope1, scope2 and scope10 sort by their number */
export const compareNames = (a: string, b: string) => nameCollator.compare(a, b);

/** Escapes a segment for use in a JSON pointer style path, e.g. a tree node id */
export const escapePointerSegment = (segment: string) => segment.replace(/~/g, '~0').replace(/\//g, '~1');

/** Whether any of `modules` is one of the selected modules */
export function hasAnySelected(modules: Iterable<unknown> | undefined, selected: ReadonlySet<ModuleId>): boolean {
  if (!modules) return false;
  for (const m of modules) {
    if (isModuleId(m) && selected.has(m)) return true;
  }
  return false;
}

/**
 * Untagged const properties are oneOf discriminators (e.g. method: "location"). They are needed to tell variants
 * apart, so they are kept alongside fields matching the selected modules.
 */
export const isDiscriminator = (schema: unknown) =>
  isObject(schema) && 'const' in schema && !Array.isArray(schema['x-modules']);

export type ResolvedSchema = {
  /** The schema with any $ref followed, keeping keywords declared alongside the $ref */
  schema: JsonObject;
  /** Name of the first component schema followed */
  refName?: string;
  /** Component schemas being expanded, outermost first, used to detect circular $refs */
  stack: readonly string[];
  /** The $ref points back at a schema already in the stack, so `schema` only holds the sibling keywords */
  circular: boolean;
};

/** Follows component schema $refs from `raw`, stopping when a $ref points back at a schema in `stack` */
export function resolveRefs(
  raw: JsonObject,
  components: JsonObject | undefined,
  stack: readonly string[],
): ResolvedSchema {
  let schema = raw;
  let refName: string | undefined;
  let refStack = stack;

  for (let name = getRefName(schema.$ref); name !== undefined; name = getRefName(schema.$ref)) {
    const { $ref: _ref, ...siblings } = schema;
    if (refStack.includes(name)) {
      return { schema: siblings, refName: refName ?? name, stack: refStack, circular: true };
    }
    const target = components?.[name];
    if (!isObject(target)) break;
    schema = { ...target, ...siblings };
    refName ??= name;
    refStack = [...refStack, name];
  }

  return { schema, refName, stack: refStack, circular: false };
}

/** The JSON schema of a request body or response, preferring application/json over other media types */
export function getJsonMediaSchema(body: unknown): JsonObject | undefined {
  const content = isObject(body) && isObject(body.content) ? body.content : undefined;
  const media = content?.['application/json'] ?? (content ? Object.values(content)[0] : undefined);
  return isObject(media) && isObject(media.schema) ? media.schema : undefined;
}

/** The JSON request body schema of an operation, if it has one */
export function getRequestBodySchema(spec: JsonObject, path: string, method: string): JsonObject | undefined {
  const pathItem = isObject(spec.paths) ? spec.paths[path] : undefined;
  const operation = isObject(pathItem) ? pathItem[method] : undefined;
  return getJsonMediaSchema(isObject(operation) ? operation.requestBody : undefined);
}
